import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useKnowledgeBases } from "@/hooks/useKnowledgeBases";
import { FileText, Download, Eye, Calendar } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const CurtKnowledgeBases = () => {
  const navigate = useNavigate();
  const { data: knowledgeBases, isLoading } = useKnowledgeBases();

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">CURT Knowledge Bases</h1>
            <p className="text-muted-foreground">
              Browse and discover construction knowledge contributions
            </p>
          </div>
          <div className="space-x-4">
            <Button onClick={() => navigate("/curt-channel")}>
              Back to Channel
            </Button>
            <Button onClick={() => navigate("/call-for-knowledge")}>
              Upload Knowledge
            </Button>
          </div>
        </div>

        {isLoading ? (
          <p className="text-center text-muted-foreground">Loading knowledge bases...</p>
        ) : knowledgeBases && knowledgeBases.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {knowledgeBases.map((kb) => (
              <Card key={kb.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <Avatar>
                        <AvatarImage src={kb.profiles?.avatar_url} />
                        <AvatarFallback>
                          {kb.profiles?.display_name?.[0] || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {kb.profiles?.display_name || 'Unknown'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(kb.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </div>
                  <CardTitle className="mt-4 line-clamp-2">{kb.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  {kb.description && (
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                      {kb.description}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2 mb-4">
                    {kb.domains && (
                      <Badge variant="secondary">
                        {kb.domains.symbol} {kb.domains.name}
                      </Badge>
                    )}
                    {kb.is_bounty && (
                      <Badge variant="default">Bounty</Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      {kb.views_count}
                    </div>
                    <div className="flex items-center gap-1">
                      <FileText className="h-4 w-4" />
                      v{kb.version}
                    </div>
                  </div>

                  {kb.file_url && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => window.open(kb.file_url, '_blank')}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No Knowledge Bases Yet</h3>
            <p className="text-muted-foreground mb-4">
              Be the first to contribute knowledge to the CURT channel!
            </p>
            <Button onClick={() => navigate("/call-for-knowledge")}>
              Upload First Knowledge Base
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CurtKnowledgeBases;
